// StatusBar.jsx — Connection status banner + top bar with controls
// Shows connection state, account margins, demo badge, theme & density toggles

import React from 'react';
import { fmtINR, fmtNum } from './calculations';

// ═══════════════════════════════════════════════════════════
// CONNECTION STATUS BANNER — shown when not live
// ═══════════════════════════════════════════════════════════

function ConnectionBanner({ connStatus, themeCtx, onRetry }) {
  const { t, isDark } = themeCtx;
  if (!connStatus || connStatus.status === 'live') return null;

  const statusColors = {
    'market-closed': { bg: isDark ? '#1a1d2a' : '#f0f4ff', text: isDark ? '#8b8fa3' : '#6b7280', dot: isDark ? '#4a5568' : '#9ca3af' },
    'delayed':       { bg: isDark ? '#2e2a1a' : '#fffbeb', text: '#fbbf24', dot: '#fbbf24' },
    'stale':         { bg: isDark ? '#2e2a1a' : '#fffbeb', text: '#f97316', dot: '#f97316' },
    'initializing':  { bg: isDark ? '#1a1d2a' : '#f0f4ff', text: isDark ? '#8b8fa3' : '#6b7280', dot: '#3b82f6' },
  };
  const fallback = { bg: isDark ? '#2e1a1a' : '#fef2f2', text: '#ef4444', dot: '#ef4444' };
  const colors = statusColors[connStatus.status] || fallback;

  return (
    <div style={{
      display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
      padding: '6px 16px', fontSize: 12, fontWeight: 500,
      background: colors.bg, color: colors.text,
      borderBottom: `1px solid ${t.border}`
    }}>
      <span style={{
        width: 8, height: 8, borderRadius: '50%', flexShrink: 0,
        background: colors.dot,
        animation: connStatus.status === 'initializing' ? 'pulse 1.5s ease-in-out infinite' : 'none'
      }} />
      <span>{connStatus.message}</span>
      {connStatus.status === 'offline' && (
        <button
          onClick={onRetry}
          style={{ marginLeft: 8, padding: '2px 12px', fontSize: 11, background: 'rgba(255,255,255,0.15)', color: 'inherit', border: '1px solid rgba(255,255,255,0.2)', borderRadius: 4, cursor: 'pointer' }}
        >
          Retry
        </button>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════
// TOP BAR — title, connection indicator, account margins, controls
// ═══════════════════════════════════════════════════════════

function TopBar({ connStatus, themeCtx, demoMode, initialized, marginFetchMode, accountMargins, theme, setTheme, density, setDensity }) {
  const { t, isDark } = themeCtx;
  const statusLabel = {
    'live': 'Live', 'delayed': 'Delayed', 'stale': 'Stale',
    'market-closed': 'Closed', 'initializing': 'Starting...'
  };
  const statusDotColor = {
    'live': t.green, 'delayed': t.yellow, 'stale': '#f97316',
    'market-closed': isDark ? '#4a5568' : '#9ca3af', 'initializing': t.blue
  };

  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 16px', borderBottom: `1px solid ${t.border}`, background: t.headerBg, flexShrink: 0 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <span style={{ fontSize: 15, fontWeight: 700 }}>Market Watch</span>
        {connStatus && (
          <span style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 11, color: t.textMuted }}>
            <span style={{
              width: 7, height: 7, borderRadius: '50%', display: 'inline-block',
              background: statusDotColor[connStatus.status] || t.red
            }} />
            {statusLabel[connStatus.status] || 'Offline'}
            {connStatus.details?.wsTokens > 0 && connStatus.status === 'live' && (
              <span style={{ marginLeft: 4 }}>({fmtNum(connStatus.details.wsTokens)} RT)</span>
            )}
            {connStatus.details?.failoverActive && (
              <span style={{ marginLeft: 4, color: t.yellow }}>(Kotak backup)</span>
            )}
            {connStatus.details?.displayOnly && (
              <span style={{ marginLeft: 8, fontSize: 10, color: t.blue, fontWeight: 600, padding: '1px 6px', background: isDark ? '#1a2436' : '#eff6ff', borderRadius: 3 }}>
                Yesterday's closing data — live at 8:50
              </span>
            )}
            {connStatus.details?.staleList && (
              <span style={{ marginLeft: 8, fontSize: 10, color: '#f97316', fontWeight: 600, padding: '1px 6px', background: isDark ? '#2e2a1a' : '#fff7ed', borderRadius: 3 }}>
                Built from yesterday's instrument list
              </span>
            )}
            {marginFetchMode === 'fallback' && (
              <span style={{ marginLeft: 8, fontSize: 10, color: '#f97316', fontWeight: 600, padding: '1px 6px', background: isDark ? '#2e2a1a' : '#fff7ed', borderRadius: 3 }}>Margins: slow fetch (batch failed)</span>
            )}
          </span>
        )}
        {demoMode && (
          <span style={{ fontSize: 10, color: t.yellow, fontWeight: 600, padding: '1px 6px', background: isDark ? '#2e2a1a' : '#fffbeb', borderRadius: 3 }}>DEMO</span>
        )}
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: 12, fontSize: 11, color: t.textMuted }}>
        {Object.entries(accountMargins).map(([id, acct]) => (
          <span key={id}>
            {acct.label || id}: <span style={{ color: t.text, fontWeight: 500 }}>{fmtINR(acct.available || 0)}</span>
          </span>
        ))}

        <button
          onClick={() => setTheme(th => th === 'dark' ? 'light' : 'dark')}
          style={{ background: t.bgInput, border: `1px solid ${t.borderInput}`, borderRadius: 4, padding: '3px 8px', fontSize: 11, color: t.textMuted, cursor: 'pointer' }}
        >
          {isDark ? '☀️' : '🌙'}
        </button>
        <select
          value={density}
          onChange={e => setDensity(e.target.value)}
          style={{ background: t.bgInput, border: `1px solid ${t.borderInput}`, borderRadius: 4, padding: '3px 6px', fontSize: 11, color: t.textMuted, cursor: 'pointer' }}
        >
          <option value="compact">Compact</option>
          <option value="comfortable">Comfortable</option>
        </select>
      </div>
    </div>
  );
}

export { ConnectionBanner, TopBar };
